/**
 * The fifteen high-frequency irregular verbs (BRIEF <spanish_correctness> layer 2).
 *
 * ser, estar, ir, haber, tener, hacer, poder, decir, ver, dar, saber, querer, poner, venir, salir.
 *
 * Data, not logic. Every form is written out rather than derived, because the derivation rules for
 * these verbs are exactly as long as the table and considerably easier to get wrong. `conjugate()`
 * in `conjugation.ts` consults this first and falls back to the regular machinery.
 *
 * **Five persons, no vosotros** (DECISIONS D-066).
 *
 * Only genuinely irregular slots appear here. A verb regular in a tense is simply absent from that
 * tense and falls through — `salir` has a regular preterite, and twelve of the fifteen have a
 * regular imperfect. Writing those out anyway would be fifty chances to introduce a typo in forms
 * the regular tables already produce correctly.
 *
 * T31a: present. T31b: preterite. T31c: imperfect and conditional. T31d: present subjunctive.
 */

import type { Conjugation, Person, Tense } from './conjugation';

export const IRREGULAR_VERBS = [
  'ser',
  'estar',
  'ir',
  'haber',
  'tener',
  'hacer',
  'poder',
  'decir',
  'ver',
  'dar',
  'saber',
  'querer',
  'poner',
  'venir',
  'salir',
] as const;

export type IrregularVerb = (typeof IRREGULAR_VERBS)[number];

const VERB_SET: ReadonlySet<string> = new Set(IRREGULAR_VERBS);

export function isIrregular(infinitive: string): infinitive is IrregularVerb {
  return VERB_SET.has(infinitive.toLowerCase());
}

/** Sparse: a tense a verb conjugates regularly is simply absent. */
type SparseTable = Partial<Record<Tense, Conjugation>>;

// ---------------------------------------------------------------------------------------------
// T31a — present
// ---------------------------------------------------------------------------------------------

const PRESENT: Record<IrregularVerb, Conjugation> = {
  ser: { yo: 'soy', tu: 'eres', el: 'es', nosotros: 'somos', ustedes: 'son' },
  estar: { yo: 'estoy', tu: 'estás', el: 'está', nosotros: 'estamos', ustedes: 'están' },
  ir: { yo: 'voy', tu: 'vas', el: 'va', nosotros: 'vamos', ustedes: 'van' },
  haber: { yo: 'he', tu: 'has', el: 'ha', nosotros: 'hemos', ustedes: 'han' },
  tener: { yo: 'tengo', tu: 'tienes', el: 'tiene', nosotros: 'tenemos', ustedes: 'tienen' },
  hacer: { yo: 'hago', tu: 'haces', el: 'hace', nosotros: 'hacemos', ustedes: 'hacen' },
  poder: { yo: 'puedo', tu: 'puedes', el: 'puede', nosotros: 'podemos', ustedes: 'pueden' },
  decir: { yo: 'digo', tu: 'dices', el: 'dice', nosotros: 'decimos', ustedes: 'dicen' },
  ver: { yo: 'veo', tu: 'ves', el: 've', nosotros: 'vemos', ustedes: 'ven' },
  dar: { yo: 'doy', tu: 'das', el: 'da', nosotros: 'damos', ustedes: 'dan' },
  // `sé` carries a tilde only to keep it apart from the pronoun `se`. Nothing about the stress
  // rules asks for it — it is a diacrítica, which is why the accent module does not predict it.
  saber: { yo: 'sé', tu: 'sabes', el: 'sabe', nosotros: 'sabemos', ustedes: 'saben' },
  querer: { yo: 'quiero', tu: 'quieres', el: 'quiere', nosotros: 'queremos', ustedes: 'quieren' },
  poner: { yo: 'pongo', tu: 'pones', el: 'pone', nosotros: 'ponemos', ustedes: 'ponen' },
  venir: { yo: 'vengo', tu: 'vienes', el: 'viene', nosotros: 'venimos', ustedes: 'vienen' },
  salir: { yo: 'salgo', tu: 'sales', el: 'sale', nosotros: 'salimos', ustedes: 'salen' },
};

// ---------------------------------------------------------------------------------------------
// T31b — preterite
// ---------------------------------------------------------------------------------------------

/**
 * The *pretérito fuerte*: a changed stem, and endings stressed on the STEM rather than the ending.
 *
 * That stress shift is the whole shape of it, and the reason none of these carry a written accent
 * where the regular paradigm does: `tuve`/`tuvo`, never `tuvé`/`tuvó`, against regular
 * `hablé`/`habló`. A learner who writes `tuvé` has over-applied the regular rule, which is a
 * named error the classifier can report rather than a bare "wrong".
 *
 * `salir` is absent: its preterite is entirely regular.
 */
const PRETERITE: Partial<Record<IrregularVerb, Conjugation>> = {
  // ser and ir share the whole preterite. Genuinely ambiguous — `fui` is both, and no amount of
  // context inside a single word will separate them.
  ser: { yo: 'fui', tu: 'fuiste', el: 'fue', nosotros: 'fuimos', ustedes: 'fueron' },
  ir: { yo: 'fui', tu: 'fuiste', el: 'fue', nosotros: 'fuimos', ustedes: 'fueron' },
  estar: {
    yo: 'estuve',
    tu: 'estuviste',
    el: 'estuvo',
    nosotros: 'estuvimos',
    ustedes: 'estuvieron',
  },
  haber: { yo: 'hube', tu: 'hubiste', el: 'hubo', nosotros: 'hubimos', ustedes: 'hubieron' },
  tener: { yo: 'tuve', tu: 'tuviste', el: 'tuvo', nosotros: 'tuvimos', ustedes: 'tuvieron' },
  // `hizo`, not `hico`: c becomes z before o to keep the sound, the same orthographic logic the
  // regular tables apply in reverse.
  hacer: { yo: 'hice', tu: 'hiciste', el: 'hizo', nosotros: 'hicimos', ustedes: 'hicieron' },
  poder: { yo: 'pude', tu: 'pudiste', el: 'pudo', nosotros: 'pudimos', ustedes: 'pudieron' },
  // A j-stem swallows the i of the third-person plural: `dijeron`, never `dijieron`.
  decir: { yo: 'dije', tu: 'dijiste', el: 'dijo', nosotros: 'dijimos', ustedes: 'dijeron' },
  // `vi`, `vio`, `di`, `dio` are monosyllables and take no tilde (RAE, 2010).
  ver: { yo: 'vi', tu: 'viste', el: 'vio', nosotros: 'vimos', ustedes: 'vieron' },
  dar: { yo: 'di', tu: 'diste', el: 'dio', nosotros: 'dimos', ustedes: 'dieron' },
  saber: { yo: 'supe', tu: 'supiste', el: 'supo', nosotros: 'supimos', ustedes: 'supieron' },
  querer: { yo: 'quise', tu: 'quisiste', el: 'quiso', nosotros: 'quisimos', ustedes: 'quisieron' },
  poner: { yo: 'puse', tu: 'pusiste', el: 'puso', nosotros: 'pusimos', ustedes: 'pusieron' },
  venir: { yo: 'vine', tu: 'viniste', el: 'vino', nosotros: 'vinimos', ustedes: 'vinieron' },
};

// ---------------------------------------------------------------------------------------------
// T31c — imperfect
// ---------------------------------------------------------------------------------------------

/**
 * Three verbs. That is not "three of these fifteen" — **`ser`, `ir` and `ver` are the only verbs
 * in the entire language with an irregular imperfect**, which makes the imperfect the most
 * reliable tense in Spanish and worth telling the learner so.
 *
 * `ver` is the mild one: it keeps the `e` of the old infinitive *veer*, so `veía` rather than the
 * regular `vía`.
 */
const IMPERFECT: Partial<Record<IrregularVerb, Conjugation>> = {
  ser: { yo: 'era', tu: 'eras', el: 'era', nosotros: 'éramos', ustedes: 'eran' },
  ir: { yo: 'iba', tu: 'ibas', el: 'iba', nosotros: 'íbamos', ustedes: 'iban' },
  ver: { yo: 'veía', tu: 'veías', el: 'veía', nosotros: 'veíamos', ustedes: 'veían' },
};

// ---------------------------------------------------------------------------------------------
// T31c — conditional
// ---------------------------------------------------------------------------------------------

/**
 * Contracted stems. The endings are the regular `-ía` set throughout — only the stem the suffix
 * attaches to changes, which is why this is stored as ten stems rather than fifty forms.
 *
 * Three families, and naming them is most of the teaching:
 *  - **drop the vowel** — haber → habr, poder → podr, saber → sabr, querer → querr;
 *  - **vowel becomes d** — tener → tendr, poner → pondr, venir → vendr, salir → saldr;
 *  - **short and irregular** — hacer → har, decir → dir.
 *
 * ser, estar, ir, ver and dar are absent: their conditionals are the plain infinitive plus `-ía`.
 * These same stems form the future, which is not in this build's tense set.
 */
const CONDITIONAL_STEMS: Partial<Record<IrregularVerb, string>> = {
  haber: 'habr',
  poder: 'podr',
  saber: 'sabr',
  querer: 'querr',
  tener: 'tendr',
  poner: 'pondr',
  venir: 'vendr',
  salir: 'saldr',
  hacer: 'har',
  decir: 'dir',
};

/** The one ending set the conditional uses, whatever the stem. */
const CONDITIONAL_ENDINGS: Conjugation = {
  yo: 'ía',
  tu: 'ías',
  el: 'ía',
  nosotros: 'íamos',
  ustedes: 'ían',
};

function conditionalFrom(stem: string): Conjugation {
  return {
    yo: stem + CONDITIONAL_ENDINGS.yo,
    tu: stem + CONDITIONAL_ENDINGS.tu,
    el: stem + CONDITIONAL_ENDINGS.el,
    nosotros: stem + CONDITIONAL_ENDINGS.nosotros,
    ustedes: stem + CONDITIONAL_ENDINGS.ustedes,
  };
}

// ---------------------------------------------------------------------------------------------
// T31d — present subjunctive
// ---------------------------------------------------------------------------------------------

/**
 * All fifteen. Unlike the imperfect, nothing here falls through.
 *
 * Nine of them are not really irregular at all once you know the rule: **take the present `yo`
 * form, drop the `-o`, add the opposite-vowel endings.** `tengo` → `tenga`, `hago` → `haga`,
 * `digo` → `diga`, `pongo` → `ponga`, `vengo` → `venga`, `salgo` → `salga`, `veo` → `vea`, and the
 * two boot verbs on their diphthongised stem. That rule is the single most productive thing in the
 * Spanish subjunctive and is worth surfacing in feedback rather than presenting these as fifteen
 * things to memorise.
 *
 * Six break it, and they are the six worth memorising: **dar, ir, ser, haber, estar, saber**.
 *
 * The two boot verbs keep the plain stem at `nosotros` — `podamos`, `queramos` — exactly as they
 * do in the present.
 */
const SUBJUNCTIVE: Record<IrregularVerb, Conjugation> = {
  // The six that do not come from the `yo` form.
  ser: { yo: 'sea', tu: 'seas', el: 'sea', nosotros: 'seamos', ustedes: 'sean' },
  // `dé` carries a tilde only to keep it apart from the preposition `de`; `demos` and `den` have
  // nothing to be confused with and take none.
  dar: { yo: 'dé', tu: 'des', el: 'dé', nosotros: 'demos', ustedes: 'den' },
  ir: { yo: 'vaya', tu: 'vayas', el: 'vaya', nosotros: 'vayamos', ustedes: 'vayan' },
  haber: { yo: 'haya', tu: 'hayas', el: 'haya', nosotros: 'hayamos', ustedes: 'hayan' },
  estar: { yo: 'esté', tu: 'estés', el: 'esté', nosotros: 'estemos', ustedes: 'estén' },
  saber: { yo: 'sepa', tu: 'sepas', el: 'sepa', nosotros: 'sepamos', ustedes: 'sepan' },

  // The nine built from the present `yo` form.
  tener: { yo: 'tenga', tu: 'tengas', el: 'tenga', nosotros: 'tengamos', ustedes: 'tengan' },
  hacer: { yo: 'haga', tu: 'hagas', el: 'haga', nosotros: 'hagamos', ustedes: 'hagan' },
  decir: { yo: 'diga', tu: 'digas', el: 'diga', nosotros: 'digamos', ustedes: 'digan' },
  poner: { yo: 'ponga', tu: 'pongas', el: 'ponga', nosotros: 'pongamos', ustedes: 'pongan' },
  venir: { yo: 'venga', tu: 'vengas', el: 'venga', nosotros: 'vengamos', ustedes: 'vengan' },
  salir: { yo: 'salga', tu: 'salgas', el: 'salga', nosotros: 'salgamos', ustedes: 'salgan' },
  ver: { yo: 'vea', tu: 'veas', el: 'vea', nosotros: 'veamos', ustedes: 'vean' },
  poder: { yo: 'pueda', tu: 'puedas', el: 'pueda', nosotros: 'podamos', ustedes: 'puedan' },
  querer: { yo: 'quiera', tu: 'quieras', el: 'quiera', nosotros: 'queramos', ustedes: 'quieran' },
};

/**
 * The six whose subjunctive cannot be derived from the present `yo` form.
 *
 * Exported because the feedback card should name them as the exceptions they are, rather than
 * leaving the learner to discover that the rule works nine times out of fifteen.
 */
export const SUBJUNCTIVE_EXCEPTIONS: readonly IrregularVerb[] = [
  'dar',
  'ir',
  'ser',
  'haber',
  'estar',
  'saber',
];

// ---------------------------------------------------------------------------------------------
// Assembly
// ---------------------------------------------------------------------------------------------

const TABLES: Record<IrregularVerb, SparseTable> = Object.fromEntries(
  IRREGULAR_VERBS.map((verb) => {
    const table: SparseTable = { present: PRESENT[verb] };
    const preterite = PRETERITE[verb];
    if (preterite) table.preterite = preterite;
    const imperfect = IMPERFECT[verb];
    if (imperfect) table.imperfect = imperfect;
    const conditionalStem = CONDITIONAL_STEMS[verb];
    if (conditionalStem) table.conditional = conditionalFrom(conditionalStem);
    table.subjunctive = SUBJUNCTIVE[verb];
    return [verb, table];
  }),
) as Record<IrregularVerb, SparseTable>;

/**
 * The irregular form, or `null` when this verb is regular in this tense and the caller should fall
 * through to the regular tables.
 */
export function irregularForm(infinitive: string, tense: Tense, person: Person): string | null {
  const key = infinitive.toLowerCase();
  if (!isIrregular(key)) return null;
  return TABLES[key][tense]?.[person] ?? null;
}

/** Which tenses of this verb are irregular. Used by tests and by the feedback card. */
export function irregularTenses(infinitive: string): Tense[] {
  const key = infinitive.toLowerCase();
  if (!isIrregular(key)) return [];
  return Object.keys(TABLES[key]) as Tense[];
}
